/**
 * Names the backend requires but the user never types. Section 2 of the
 * handoff: `definition.name` is mandatory on create/customize, so the
 * client generates and persists it. Back/Next reuse the stored name.
 */

const MAX = 200

const base = (campaignName?: string, categoryLabel?: string) =>
   campaignName?.trim() || categoryLabel?.trim() || 'Campaign'

const withSuffix = (stem: string, suffix: string) =>
   `${stem.slice(0, MAX - suffix.length)}${suffix}`

/** "Buyer campaign - Outbound content group 1" */
export const defaultMessageGroupName = (
   campaignName: string | undefined,
   categoryLabel: string | undefined,
   ordinal: number
) =>
   withSuffix(base(campaignName, categoryLabel), ` - Outbound content group ${ordinal}`)

/** "Buyer campaign - Outgoing call 1" / "- Incoming call 1" */
export const defaultScriptName = (
   campaignName: string | undefined,
   categoryLabel: string | undefined,
   direction: 'outbound' | 'inbound',
   ordinal: number
) =>
   withSuffix(
      base(campaignName, categoryLabel),
      ` - ${direction === 'outbound' ? 'Outgoing' : 'Incoming'} call ${ordinal}`
   )

/** "Buyer campaign - Reply instructions" */
export const defaultReplyInstructionsName = (
   campaignName: string | undefined,
   categoryLabel: string | undefined
) => withSuffix(base(campaignName, categoryLabel), ' - Reply instructions')

/** "Buyer campaign - Persona" */
export const defaultPersonaName = (
   campaignName: string | undefined,
   categoryLabel: string | undefined
) => withSuffix(base(campaignName, categoryLabel), ' - Persona')

/** Stable id for a pool: "buyer-default". */
export const defaultPoolId = (
   categoryLabel: string | undefined,
   businessTypeId: number
) => {
   const slug = (categoryLabel || `type-${businessTypeId}`)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
   return `${slug || `type-${businessTypeId}`}-default`
}

/** "Zillow buyers" from the filters, or a plain fallback. */
export const suggestCampaignName = (
   parts: string[],
   fallback = 'New campaign'
) => {
   const words = parts.filter(Boolean).join(' ').trim()
   if (!words) return fallback
   const name = words.charAt(0).toUpperCase() + words.slice(1)
   return name.slice(0, 80)
}
